import { sumBy } from 'lodash';
import { LABOR_VARIANCE_THRESHOLDS } from './consts';
import type { SalesBlock } from './types';

// ─── Number formatting ──────────────────────────────────────────────────────

export function fmtDollars(n: number | null | undefined): string {
  if (n == null) return '—';
  return '$' + Math.abs(n).toLocaleString('en-US');
}

export function fmtDollarsVar(n: number | null | undefined): string {
  if (n == null) return '—';
  const sign = n > 0 ? '+' : n < 0 ? '-' : '';
  return `${sign}$${Math.abs(n).toLocaleString('en-US')}`;
}

export function fmtHours(n: number | null | undefined): string {
  if (n == null) return '—';
  return n.toFixed(2);
}

export function fmtHoursVar(n: number | null | undefined): string {
  if (n == null) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(2)}`;
}

// ─── Pace helpers ───────────────────────────────────────────────────────────

/** Progress 0–1 of actual vs elapsed forecast. Capped at 1. */
export function paceProgress(actual: number, elapsedForecast: number): number {
  if (!elapsedForecast) return 0;
  return Math.min(actual / elapsedForecast, 1);
}

export function isBehindPace(actual: number, elapsedForecast: number): boolean {
  return actual < elapsedForecast;
}

export function calculateVariance(actual: number, forecast: number): number | null {
  return !actual || !forecast ? null : ((actual - forecast) / forecast) * 100;
}

export function formatVariance(variance: number): string {
  if (!variance) return '—';

  return `${Math.abs(variance).toFixed(2)}%`;
}

// ─── Operational hours slice ─────────────────────────────────────────────────

export const getLaborVarianceColors = (key: string, value: number, isHourly: boolean): { bg: string; text: string } => {
  const isLaborVarianceActual = key === 'variance_actual';
  const isLaborVarianceCumulative = key === 'variance_cumulative';
  const colors = { bg: 'white', text: 'gray.900' };

  if (isLaborVarianceActual || isLaborVarianceCumulative) {
    const thresholds = isHourly ? LABOR_VARIANCE_THRESHOLDS.sixtyMin : LABOR_VARIANCE_THRESHOLDS.thirtyMin;
    const limit = isLaborVarianceActual ? thresholds.actualToGuide : thresholds.cumulative;
    const abs = Math.abs(value);

    if (abs >= limit.red) return { bg: 'error.50', text: 'error.700' };
    if (limit.yellow !== null && abs >= limit.yellow) return { bg: 'yellow.50', text: 'yellow.700' };
  }

  return colors;
};

export const aggregateToHourSlot = (blocks: SalesBlock[]): Partial<SalesBlock> => {
  const hasActual = blocks.length > 0 && blocks.every(e => e.hasActual);
  const actuals = blocks.filter(e => e.hasActual);
  return {
    hasActual,
    salesActual: sumBy(actuals, 'salesActual'),
    salesForecast: sumBy(blocks, 'salesForecast'),
    salesVar: sumBy(actuals, 'salesVar'),
    salesCumVar: actuals[actuals.length - 1]?.salesCumVar ?? 0, // last 30-min bucket's API cumulative variance
    laborScheduled: sumBy(actuals, 'laborScheduled'),
    laborSchedSvc: sumBy(actuals, 'laborSchedSvc'),
    laborSchedNS: sumBy(actuals, 'laborSchedNS'),
    laborGuide: sumBy(actuals, 'laborGuide'),
    laborGuideSvc: sumBy(actuals, 'laborGuideSvc'),
    laborGuideNS: sumBy(actuals, 'laborGuideNS'),
    laborActual: sumBy(actuals, 'laborActual'),
    laborSalary: sumBy(actuals, 'laborSalary'),
    laborHourly: sumBy(actuals, 'laborHourly'),
    laborVar: sumBy(actuals, 'laborVar'),
    laborCumVar: actuals[actuals.length - 1]?.laborCumVar ?? 0, // last 30-min bucket's API cumulative variance
  };
};
